import React, { FC, useState, useEffect } from "react";
import styled from "styled-components";
import contentfulComponentLoop from "../lib/contentfulComponentLoop";
import MultiTab, { MultiTabProps } from "../components/MultiTab";
import { Entry } from "contentful";

interface MultiTabContainerProps extends Entry {
  id: string;
  brand: string;
  parentProps: any;
  fields: {
    heading?: string;
    tabs: [MultiTabProps];
    tabContent: [Entry];
    tabLayout: string;
    tabStyle?: string;
  };
}

const RootContainer = styled.div`
  overflow: visible;

  h2 {
    font-family: Futura;
    font-style: normal;
    font-weight: 600;
    font-size: 24px;
    line-height: 36px;
    letter-spacing: -0.48px;
    color: #191A1F;
    margin: 0;
    margin-bottom: 28px;
  }

  @media (min-width: 768px) {
    h2 {
      font-size: 32px;
      line-height: 34px;
      letter-spacing: -0.72px;
      margin-bottom: 30px;
    }
  }
`;

const TabContainer = styled.div<{ $layout: string }>`
  overflow: scroll;
  display: flex;
  gap: 4px;
  margin-bottom: 26px;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;

  @media (min-width: 1024px) {
    margin-bottom: 32px;
  }

  ${({ $layout }) => $layout == 'center' && `
    @media (min-width: 768px) {
      justify-content: center;
    }
  `}

  ${({ $layout }) => $layout == 'right' && `
    @media (min-width: 768px) {
      justify-content: flex-end;
    }
  `}
`;

const ContentContainer = styled.div<{ $activeIndex: number }>`
  ${({ $activeIndex }) => `
    > * {
      display: none;

      &:nth-child(${$activeIndex + 1}) {
        display: flex;
      }
    }
  `}
`;

const MultiTabContainer: FC<MultiTabContainerProps> = ({ id, brand, parentProps, fields: { 
  heading,
  tabContent, 
  tabLayout, 
  tabs, 
  tabStyle = 'Underlined' 
}}) => {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <RootContainer className="multi-tab-root-component" data-component-id={id}>
      {heading && <h2>{heading}</h2>}
      <TabContainer $layout={tabLayout}>
        {tabs.map((tab, index) => (
          <MultiTab
            {...tab}
            brand={brand}
            theme={parentProps.theme}
            key={tab.sys.id}
            tabStyle={tabStyle}
            isActive={tabIndex == index}
            id={tab.sys.contentType.sys.id}
            onClick={() => setTabIndex(index)}
          />
        ))}
      </TabContainer>
      <ContentContainer $activeIndex={tabIndex}>
        {contentfulComponentLoop(tabContent)}
      </ContentContainer>
    </RootContainer>
  );
};

export default MultiTabContainer;